/**
 * Hard-coded application feature flags. Edit + redeploy the frontend to flip.
 *
 * Used to dark-ship cross-cutting app behavior (auth flows, observability,
 * routing, etc.) that is merged but not yet ready to be the default.
 *
 * Unlike `integrationFeatureFlags.ts`, these flags do NOT have an env twin
 * in `src/config/config.js`. They are global app behavior, not per-tenant
 * or per-environment toggles, and we want a single source of truth. To
 * flip, change the value here, redeploy, and remove the flag once the
 * behavior is permanent.
 *
 * Adding a new flag:
 *   1. Add a key with a JSDoc explaining what behavior the flag gates.
 *   2. Default it to `false` (dark).
 *   3. Read it via a helper exported from this file (e.g.
 *      `isCentralizedJwtExpirationEnabled()`).
 *   4. Document the rollout/removal plan in the PR that introduces it.
 */
export const APPLICATION_FEATURE_FLAGS = {
  /**
   * Centralized JWT-expiration handling at the axios layer. When ON, a
   * response interceptor in `src/data/axios.ts` detects 401s with the
   * backend "Authorization error:" / "Authentication error:" body shape
   * (and proactively rejects already-expired stored tokens) and dispatches
   * an app-wide `aipx:jwt-expired` event that `AuthExpirationWatcher`
   * (mounted in the root layout) consumes to perform a single
   * `router.replace("/session-timeout")`.
   *
   * Replaces the per-page opt-in via
   * `handleAppError({ checkJWTExpiration: true, router })`, which only
   * covers pages that explicitly wired it up and leaves class components
   * (e.g. `AlertView`) making indefinite 401s.
   *
   * OFF by default - prod keeps the existing behavior. Flip to `true`
   * here once the change has soaked on dev/staging via a temporary
   * deploy. After it has been ON in prod for ~1 week, a follow-up PR
   * removes the flag entirely and sweeps the legacy `checkJWTExpiration`
   * plumbing out of `errorHandler.ts` and its callsites.
   */
  CENTRALIZED_JWT_EXPIRATION_ENABLED: false
} as const;

export type ApplicationFeatureFlag = keyof typeof APPLICATION_FEATURE_FLAGS;

/**
 * Indirection so consumer modules can be swapped/mocked in tests without
 * mutating the constant, and so future flags that need richer logic
 * (user-tier checks, runtime overrides, etc.) can extend the helper
 * without churning every call site.
 */
export function isCentralizedJwtExpirationEnabled(): boolean {
  return APPLICATION_FEATURE_FLAGS.CENTRALIZED_JWT_EXPIRATION_ENABLED;
}
